import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
    CognitoIdentityProviderClient,
    ConfirmSignUpCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import OtpInput from "../otp-input/OtpInput";
import { resendVerificationCode } from "../../../utils/verificationUtils";
import classes from "../../../pages/Auth/Forms/AuthForm.module.scss";

// Props from parent: email, callback on success, callback on cancel
interface VerificationModalProps {
    email: string;
    onVerificationSuccess: () => void;
    onCancel: () => void;
}

// Ref interface to allow parent to control modal visibility
export interface VerificationModalRef {
    open: () => void;
    close: () => void;
}

// Define error type for Cognito errors
interface CognitoError {
    name: string;
    message: string;
    $metadata?: Record<string, unknown>;
}

// Initialize Cognito client
const cognitoClient = new CognitoIdentityProviderClient({
    region: "ca-central-1",
});

// Define the component with forwardRef to expose control methods to parent
const VerificationModal = forwardRef<VerificationModalRef, VerificationModalProps>(
    ({ email, onVerificationSuccess, onCancel }, ref) => {
        const [inputOTPCode, setInputOTPCode] = useState<string>("");
        const [otpError, setOtpError] = useState<string>("");
        const [isResending, setIsResending] = useState<boolean>(false); // Disable resend button during request
        const modalRef = React.useRef<HTMLDialogElement | null>(null); // Dialog ref

        // Expose `open()` and `close()` to parent component
        useImperativeHandle(ref, () => ({
            open: () => modalRef.current?.showModal(),
            close: () => modalRef.current?.close(),
        }));

        // Updates OTP input field
        const handleOtpInputChange = (code: string) => {
            setInputOTPCode(code);
            setOtpError(""); // Reset error on input
        };

        // Handles resend OTP request
        const handleResendCode = async () => {
            // If the resend button is already being clicked, don't allow it to be clicked again
            if (isResending) {
                return;
            }

            // Set the resend button to loading state
            setIsResending(true);

            try {
                await resendVerificationCode(email); // Call utility to resend code
                setOtpError("");
                setInputOTPCode(""); // Clear previous input
            } catch (error: unknown) {
                setOtpError("Failed to resend code. ☹️ Please try again.");
            } finally {
                setIsResending(false); // Reset the resend button to normal state
            }
        };

        // Confirms signup by submitting OTP to Cognito
        const handleConfirmSignup = async () => {
            // If the user hasn't entered a verification code, don't allow them to submit
            if (!inputOTPCode) {
                setOtpError("Verification code is required. 🔑");
                return;
            }

            try {
                const confirmCommand = new ConfirmSignUpCommand({
                    ClientId: "5u806hjmmcee0qu442qddug8gv", // Cognito app client ID TODO: Move to config
                    Username: email,
                    ConfirmationCode: inputOTPCode,
                });

                await cognitoClient.send(confirmCommand); // Submit confirmation to Cognito

                // On success: close modal and notify parent
                modalRef.current?.close();
                onVerificationSuccess();
            } catch (error: unknown) {
                // Handle specific Cognito error messages with proper type casting
                const cognitoError = error as CognitoError;

                if (cognitoError.name === "CodeMismatchException") {
                    setOtpError("Invalid verification code. 👎 Please try again.");
                } else if (cognitoError.name === "ExpiredCodeException") {
                    setOtpError("Verification code has expired. ⏳ Please request a new one.");
                } else {
                    setOtpError("An error occurred. ☹️ Please try again.");
                }
            }
        };

        // JSX UI rendering
        return (
            <dialog className={classes.verificationModalContainer} ref={modalRef}>
                <h3>Verify Your Email</h3>
                <p>Enter the verification code sent to {email}</p>
                <OtpInput value={inputOTPCode} onChange={handleOtpInputChange} />

                {otpError && <p className={classes.otpErrorText}>{otpError}</p>}

                <p>
                    Didn&apos;t receive code?{" "}
                    <span
                        onClick={handleResendCode}
                        className={classes.underlineText}
                        style={{ cursor: "pointer" }}
                    >
                        {isResending ? "Sending..." : "Resend"}
                    </span>
                </p>

                <div className={classes.actionBtns}>
                    <button onClick={onCancel} className={classes.cancelBtn}>
                        Cancel
                    </button>
                    <button onClick={handleConfirmSignup} className={classes.verifyBtn}>
                        Verify
                    </button>
                </div>
            </dialog>
        );
    },
);

VerificationModal.displayName = "VerificationModal";

export default VerificationModal;
